import { Action, Reducer } from "redux";
import { AppThunkAction } from "./";
import { IProjectInfo } from "./ProjectsListStore";
import { IUserInfo } from "../entities/common";
import { toDate } from "../components/utils/common";
import { GetTimeTrackingEntriesResult } from "./TimeTrackingStore";
import { post } from "../fetch-interceptor";
import { catchApiError, isCancel, isHandledError } from "./utils";
import { DelegationScope } from "./DelegationStore";
import {
    KnownAction as NotificationsKnownAction,
    actionCreators as NotificationsActionCreators,
    NotificationType
} from "./NotificationsStore";
import {
    createApprovalStore,
    ApprovalRequestType,
    ApprovalStoreState,
    StoreActions
} from "./ApprovalCenterStore";

export interface TimesheetApprovalContext {
    timesheetId: string;
    periodId: string;
    timesheetLineIds: string[];
}

export interface PeriodInfo {
    id: string;
    startDate: Date;
    endDate: Date;
}

export interface TimesheetApprovalDetails {
    period: PeriodInfo | null;
    project: IProjectInfo | null;
    totalHours: number;
    timesheetOwner: IUserInfo | null;
    isAdministrative: boolean;
}

const parseTimesheetDetailsDates = (details: TimesheetApprovalDetails) => {
    if (details.period) {
        details.period.startDate = toDate(details.period.startDate)!;
        details.period.endDate = toDate(details.period.endDate)!;
    }
};

const timesheetStore = createApprovalStore<TimesheetApprovalDetails>(
    ApprovalRequestType.Timesheet,
    "TIMESHEET_APPROVALS",
    parseTimesheetDetailsDates,
    DelegationScope.TimesheetApproval
);

export enum TimesheetEditBlockReasons {
    None = 0,
    MultipleProjects = 1,
    AdministrativeTime = 2,
    ClosedPeriods = 4,
    SourceProjectTimeTrackingDisabled = 8,
    NoResourcePermission = 16
}

export enum TimesheetEditRowKind {
    Task = 0,
    Custom = 1,
    ProjectWork = 2,
    AdministrativeCategory = 3
}

export interface TimesheetEditTargetLine {
    projectId?: string | null;
    isAdministrative: boolean;
}

export enum TimesheetEditWarningCode {
    ResourceNotCommitted = 0,
    UnassignedTaskBlocked = 1,
    UnassignedTaskAllowed = 2
}

export interface TimesheetEditPeriodRange {
    startDate: string;
    endDate: string;
}

export interface TimesheetEditTarget {
    kind: TimesheetEditRowKind;
    taskId?: string | null;
    customTimeEntryName?: string | null;
    administrativeCategoryId?: string | null;
}

export interface TimesheetEditRow {
    key: string;
    kind: TimesheetEditRowKind;
    label: string | null;
    hours: number;
    entryIds: string[];
    movingResources: { id: string; fullName: string }[];
    defaultTarget: TimesheetEditTarget | null;
}

export interface TimesheetEditPrepareResult {
    blockReason: TimesheetEditBlockReasons;
    sourceLine: TimesheetEditTargetLine;
    sourceProject: IProjectInfo | null;
    timesheetOwner: IUserInfo | null;
    period: PeriodInfo;
    rows: TimesheetEditRow[] | null;
    timeEntries: GetTimeTrackingEntriesResult | null;
}

export interface TimesheetBulkEditPrepareResult {
    blockReason: TimesheetEditBlockReasons;
    multipleProjectNames: string[] | null;
    closedPeriods: TimesheetEditPeriodRange[] | null;
    sourceProject: { id: string; name: string } | null;
    sourceRows: TimesheetEditRow[] | null;
}

export interface TimesheetEditMappingItem {
    sourceRowKey: string;
    target: TimesheetEditTarget;
    hoursByDay?: TimesheetEditDayHours[];
}

export interface TimesheetEditDayHours {
    date: string;
    sourceHours: number;
    targetHours: number;
}

export interface TimesheetEditWarning {
    sourceRowKey: string;
    code: TimesheetEditWarningCode;
    resourceNames: string[];
}

export interface TimesheetEditValidateResult {
    warnings: TimesheetEditWarning[];
    canApply: boolean;
}

export enum TimesheetEditUnitStatus {
    Moved = 0,
    SkippedTargetHasLockedTimesheet = 1,
    SkippedNoResourcePermission = 2,
    Failed = 3
}

export interface TimesheetEditUnitResult {
    resourceId: string;
    periodId: string;
    status: TimesheetEditUnitStatus;
    error: string | null;
}

export interface TimesheetEditApplyResult {
    units: TimesheetEditUnitResult[];
}

export interface TimeTrackingEntriesState {
    isLoading: boolean;
    error: string | null;
    result: GetTimeTrackingEntriesResult | null;
}

export type TimesheetApprovalState = ApprovalStoreState<TimesheetApprovalDetails> & {
    timeEntries: TimeTrackingEntriesState;
};

const TimeEntriesActionTypes = {
    LOAD: "TIMESHEET_APPROVALS/LOAD_TIME_ENTRIES",
    LOAD_SUCCESS: "TIMESHEET_APPROVALS/LOAD_TIME_ENTRIES_SUCCESS",
    LOAD_FAILURE: "TIMESHEET_APPROVALS/LOAD_TIME_ENTRIES_FAILURE",
    CLEAR: "TIMESHEET_APPROVALS/CLEAR_TIME_ENTRIES"
};

type TimeEntriesActions = {
    type: string;
    result?: GetTimeTrackingEntriesResult;
    error?: string;
};

const reloadRequests = (dispatch: (action: AppThunkAction<StoreActions<TimesheetApprovalDetails>>) => void) => {
    dispatch(timesheetStore.actionCreators.loadRequests());
    dispatch(timesheetStore.actionCreators.loadPendingCount());
};

const reloadRequestsIfChanged = (
    dispatch: (action: AppThunkAction<StoreActions<TimesheetApprovalDetails>>) => void,
    result: TimesheetEditApplyResult
) => {
    const changed = result.units.some(
        _ => _.status === TimesheetEditUnitStatus.Moved || _.status === TimesheetEditUnitStatus.Failed
    );
    if (changed) {
        reloadRequests(dispatch);
    }
};

const parseEditPrepareResultDates = (result: TimesheetEditPrepareResult) => {
    result.period.startDate = toDate(result.period.startDate)!;
    result.period.endDate = toDate(result.period.endDate)!;
    result.timeEntries?.entries.forEach(_ => {
        _.attributes.Date = toDate(_.attributes.Date)!;
    });
};

export const actionCreators = {
    ...timesheetStore.actionCreators,

    bulkEditPrepare:
        (
            requestIds: string[],
            callback?: (result: TimesheetBulkEditPrepareResult) => void
        ): AppThunkAction<NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetBulkEditPrepareResult>("api/approval-center/timesheets/bulk-edit/prepare", { requestIds })
                .then(result => callback?.(result))
                .catch(
                    catchApiError(_ =>
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }))
                    )
                );
        },

    bulkEditValidate:
        (
            requestIds: string[],
            targetProjectId: string,
            mapping: TimesheetEditMappingItem[],
            callback?: (result: TimesheetEditValidateResult) => void
        ): AppThunkAction<NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetEditValidateResult>("api/approval-center/timesheets/bulk-edit/validate", {
                requestIds,
                targetProjectId,
                mapping
            })
                .then(result => callback?.(result))
                .catch(
                    catchApiError(_ =>
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }))
                    )
                );
        },

    bulkEditApply:
        (
            requestIds: string[],
            targetProjectId: string,
            mapping: TimesheetEditMappingItem[],
            callback?: (result: TimesheetEditApplyResult) => void,
            onError?: () => void
        ): AppThunkAction<StoreActions<TimesheetApprovalDetails> | NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetEditApplyResult>("api/approval-center/timesheets/bulk-edit/apply", {
                requestIds,
                targetProjectId,
                mapping
            })
                .then(result => {
                    reloadRequestsIfChanged(dispatch, result);
                    callback?.(result);
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        reloadRequests(dispatch);
                        onError?.();
                    })
                );
        },

    editPrepare:
        (requestId: string, callback?: (result: TimesheetEditPrepareResult) => void): AppThunkAction<NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetEditPrepareResult>(`api/approval-center/timesheets/${requestId}/edit/prepare`, {})
                .then(result => {
                    parseEditPrepareResultDates(result);
                    callback?.(result);
                })
                .catch(
                    catchApiError(_ =>
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }))
                    )
                );
        },

    editValidate:
        (
            requestId: string,
            targetLine: TimesheetEditTargetLine,
            mapping: TimesheetEditMappingItem[],
            callback?: (result: TimesheetEditValidateResult) => void
        ): AppThunkAction<NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetEditValidateResult>(`api/approval-center/timesheets/${requestId}/edit/validate`, {
                targetLine,
                mapping
            })
                .then(result => callback?.(result))
                .catch(
                    catchApiError(_ =>
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }))
                    )
                );
        },

    editApply:
        (
            requestId: string,
            targetLine: TimesheetEditTargetLine,
            mapping: TimesheetEditMappingItem[],
            callback?: (result: TimesheetEditApplyResult) => void,
            onError?: () => void
        ): AppThunkAction<StoreActions<TimesheetApprovalDetails> | NotificationsKnownAction> =>
        dispatch => {
            post<TimesheetEditApplyResult>(`api/approval-center/timesheets/${requestId}/edit/apply`, { targetLine, mapping })
                .then(result => {
                    reloadRequestsIfChanged(dispatch, result);
                    callback?.(result);
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        reloadRequests(dispatch);
                        onError?.();
                    })
                );
        },

    loadTimeEntries:
        (requestId: string): AppThunkAction<TimeEntriesActions> =>
        (dispatch, getState) => {
            dispatch({ type: TimeEntriesActionTypes.LOAD });
            timesheetStore.requestApi(dispatch, getState)
                .get<GetTimeTrackingEntriesResult>(`api/approval-center/timesheets/${requestId}/time-entries`)
                .then(result => {
                    result.entries.forEach(_ => {
                        _.attributes.Date = toDate(_.attributes.Date)!;
                    });
                    dispatch({ type: TimeEntriesActionTypes.LOAD_SUCCESS, result });
                })
                .catch(error => {
                    if (isCancel(error)) {
                        return;
                    }
                    dispatch({
                        type: TimeEntriesActionTypes.LOAD_FAILURE,
                        error: isHandledError(error)
                            ? "Delegation is no longer active."
                            : error?.response?.data?.errorMessage ?? "Failed to load time entries"
                    });
                });
        },

    clearTimeEntries: (): TimeEntriesActions => ({ type: TimeEntriesActionTypes.CLEAR })
};

const _timeEntriesInitialState: TimeTrackingEntriesState = { isLoading: true, error: null, result: null };

const timeEntriesReducer = (
    state: TimeTrackingEntriesState = _timeEntriesInitialState,
    action: TimeEntriesActions
): TimeTrackingEntriesState => {
    switch (action.type) {
        case TimeEntriesActionTypes.LOAD:
        case TimeEntriesActionTypes.CLEAR:
            return _timeEntriesInitialState;
        case TimeEntriesActionTypes.LOAD_SUCCESS:
            return { isLoading: false, error: null, result: action.result! };
        case TimeEntriesActionTypes.LOAD_FAILURE:
            return { isLoading: false, error: action.error!, result: null };
        default:
            return state;
    }
};

export const reducer: Reducer<TimesheetApprovalState> = (state, action) => {
    const base = timesheetStore.reducer(state, action);
    const timeEntries = timeEntriesReducer(state?.timeEntries, action as TimeEntriesActions);

    return state && base === state && timeEntries === state.timeEntries
        ? state
        : { ...base, timeEntries };
};
